import {
  Box, Flex, Text, Icon,
} from '@chakra-ui/react';
import { LuCheck, LuX, LuLock } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { roleSummary } from '../../utils/roleSummary';
import useIsPhone from '../../hooks/useIsPhone';

function RoleList({ title, items, granted }) {
  const isPhone = useIsPhone();
  return (
    <Box
      flex="1"
      minW={0}
      p="16px"
      borderWidth="1px"
      borderColor="redesign.border"
      borderRadius="14px"
      bg="redesign.bg.surface"
    >
      <Text textStyle="microLabel" mb="10px">
        {title}
      </Text>
      <Box as="ul" listStyleType="none" m={0} p={0}>
        {items.map((item) => (
          <Flex as="li" key={item} align="flex-start" gap="10px" py="5px">
            <Flex
              boxSize="20px"
              borderRadius="full"
              align="center"
              justify="center"
              flexShrink={0}
              mt="1px"
              bg={granted ? 'rgba(21, 128, 61, 0.10)' : 'redesign.bg.hover'}
              _dark={granted ? { bg: 'rgba(74, 222, 128, 0.14)' } : undefined}
              color={granted ? 'redesign.amount.income' : (isPhone ? 'phone.muted' : 'redesign.text.muted')}
            >
              <Icon as={granted ? LuCheck : LuX} boxSize="12px" strokeWidth={2.4} />
            </Flex>
            <Text fontSize={isPhone ? '15px' : '14px'} lineHeight="1.45" color="redesign.text">
              {item}
            </Text>
          </Flex>
        ))}
      </Box>
    </Box>
  );
}

/**
 * The guest flow's second stop: what the role lets them do in the host's
 * space. The two lists come from `roleSummary`, which derives them from
 * `auth/permissions.js`, so the screen cannot promise what the API refuses.
 */
export default function GuestRoleStep({ role, roleLabel, name }) {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const summary = roleSummary(role);
  const muted = isPhone ? 'phone.muted' : 'redesign.text.muted';

  return (
    <>
      <Text fontSize={isPhone ? '18px' : '17px'} fontWeight={700} letterSpacing="-0.01em" color="redesign.heading" mb="4px">
        {t('guest.role.lead', { role: roleLabel })}
      </Text>
      <Text fontSize="14px" lineHeight="1.6" color={muted} mb="20px" maxW="480px">
        {t(`guest.role.tagline.${role}`, { name, defaultValue: '' })}
      </Text>

      {/* Side by side the two lists fit the card; on the phone they stack. */}
      <Flex gap="12px" direction={isPhone ? 'column' : 'row'} align="stretch">
        <RoleList
          granted
          title={t('guest.role.canTitle')}
          items={summary.can.map((k) => t(`guest.role.can.${k}`))}
        />
        <RoleList
          title={t('guest.role.cannotTitle')}
          items={summary.cannot.map((k) => t(`guest.role.cannot.${k}`))}
        />
      </Flex>

      <Flex align="center" gap="10px" mt="18px">
        <Icon as={LuLock} boxSize="16px" strokeWidth={1.8} color={muted} flexShrink={0} />
        <Text fontSize="13px" lineHeight="1.5" color={muted}>
          {t('guest.role.privacy', { name })}
        </Text>
      </Flex>
    </>
  );
}
